import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { generateReport } from "../api/reportApi";
import { useParams } from "react-router-dom";
import { ReportFormField } from "./ReportFormField";

export function ReportForm({ data, isError, error }) {
  const { report } = useParams();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({});

  const mutation = useMutation({
    mutationFn: generateReport,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['last-reports'] });
        queryClient.invalidateQueries({ queryKey: ['report-current-status'] });
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked, options, files } = e.target;
    let newValue;
    if (type === 'checkbox') {
        newValue = checked;
    } else if (type === 'select-multiple') {
        newValue = Array.from(options).filter(o => o.selected).map(o => o.value);
    } else if (type === 'file') {
        newValue = files[0];
    } else {
        newValue = value;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  useEffect(() => {
    setFormData({});
    mutation.reset();
  }, [report]);

  useEffect(() => {
    if (!data?.fields) return;

    const form_initial_data = data.fields.reduce((acc, field) => {
      acc[field.name] = field.initial_data;
      return acc;
    }, {});
    setFormData(form_initial_data);
  }, [data]);

  const error_msg = isError ? "Initializing error: " + error : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({ queryKey: ['generate-report', report, formData] });
  };

  const isFormValid = () => {
    if (!data?.fields) return true;
    return data.fields.every(field => {
      if (!field.required) return true;
      const value = formData[field.name];
      if (field.type === 'BooleanField') {
        return !!value;
      }
      if (field.type === 'MultipleChoiceField') {
        return Array.isArray(value) && value.length > 0;
      }
      if (field.type === 'FileField') {
        return value !== undefined && value !== null;
      }
      return value !== undefined && value !== null && value !== '';
    });
  };

  const server_error = mutation.isError ? mutation.error.message : null;

  return (
    <>
      {error_msg && <Alert variant="danger">{error_msg}</Alert>}
      {server_error && <Alert variant="danger">{server_error}</Alert>}
      {mutation.isSuccess && <Alert variant="success">Report generation started!</Alert>}
      <Form className="d-grid gap-3" onSubmit={handleSubmit}>
        {data?.fields?.map((field) => (
          <ReportFormField
            key={field.name}
            field={field}
            formData={formData}
            onChange={handleChange}
          />
        ))}
        {data?.fields && (
          <Button variant="success" type="submit" disabled={mutation.isPending || !isFormValid()}>
            {mutation.isPending ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Generating...
              </>
            ) : 'Generate report'}
          </Button>
        )}
      </Form>
    </>
  );
}
